import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Trophy,
    TrendingUp,
    TrendingDown,
    AlertTriangle,
    CheckCircle,
    RefreshCw,
    ArrowRight,
    Building2,
    ChevronRight,
    HelpCircle,
} from 'lucide-react';

import { apiClient, ApiError, SiteRiskLeaderboardEntry } from '../services/api';
import { useScoringConfig } from '../contexts/ScoringConfigContext';
import RefreshErrorBanner from './RefreshErrorBanner';

interface SitesWidgetProps {
    onViewAll: () => void;
    refreshKey?: number;
}

/**
 * Compact Site Risk Widget for Dashboard
 * Shows risk distribution summary + top performers + sites needing attention
 * Fixed height, scalable for any number of sites
 */
const SitesWidget: React.FC<SitesWidgetProps> = ({ onViewAll, refreshKey }) => {
    const navigate = useNavigate();
    const { lowMediumBoundary, mediumHighBoundary } = useScoringConfig();
    const [leaderboard, setLeaderboard] = useState<SiteRiskLeaderboardEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const handleSiteClick = (siteId: string) => {
        navigate(`/sites?site=${siteId}`);
    };

    const loadLeaderboard = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.getSiteRiskLeaderboard();
            setLeaderboard(response.data);
        } catch (err: unknown) {
            setError(err instanceof Error ? err : new Error('Failed to load site risk data'));
            console.error('Error loading leaderboard:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadLeaderboard();
    }, [loadLeaderboard, refreshKey]);

    // Calculate risk distribution
    // Only consider sites with drivers for meaningful rankings
    const sitesWithDrivers = leaderboard.filter(s => s.totalDrivers > 0);
    const sitesWithoutDrivers = leaderboard.filter(s => s.totalDrivers === 0);

    // Risk distribution (only sites with drivers)
    const riskDistribution = {
        low: sitesWithDrivers.filter(s => s.riskLevel === 'low').length,
        medium: sitesWithDrivers.filter(s => s.riskLevel === 'medium').length,
        high: sitesWithDrivers.filter(s => s.riskLevel === 'high').length,
        noDrivers: sitesWithoutDrivers.length,
    };

    // Get top 3 performers (lowest scores, must have drivers AND be low risk)
    const topPerformers = sitesWithDrivers
        .filter(s => s.riskLevel === 'low')
        .slice(0, 3);
    const topPerformerIds = new Set(topPerformers.map(s => s.siteId));

    // Get sites needing attention (medium/high risk, sorted by score descending)
    // Exclude sites already shown in top performers to avoid duplication
    // Show more items if there are no top performers (adaptive layout)
    // Limit to 8 for compact 2-column grid (4 rows)
    const maxNeedsAttention = topPerformers.length === 0 ? 8 : 3;
    const needsAttention = sitesWithDrivers
        .filter(s => (s.riskLevel === 'medium' || s.riskLevel === 'high') && !topPerformerIds.has(s.siteId))
        .sort((a, b) => b.totalScore - a.totalScore)
        .slice(0, maxNeedsAttention);

    const getRiskColor = (level: string): string => {
        switch (level) {
            case 'low': return '#69BE28';
            case 'medium': return '#f59e0b';
            case 'high': return '#ef4444';
            default: return '#6b7280';
        }
    };

    // Only show trend icon when there's actual change
    const getTrendIcon = (trend: string) => {
        switch (trend) {
            case 'improving':
                return <span className="group relative inline-flex"><TrendingDown className="w-3 h-3 text-green-600" /><span className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">Improving</span></span>;
            case 'worsening':
                return <span className="group relative inline-flex"><TrendingUp className="w-3 h-3 text-red-600" /><span className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">Worsening</span></span>;
            default:
                return null; // Don't show anything for stable
        }
    };


    const rateLimited = error instanceof ApiError && error.status === 429;
    const refreshErrorMessage = rateLimited
        ? `Updates paused.${error.retryAfterSeconds ? ` Try again in ${error.retryAfterSeconds} seconds.` : ' Please try again shortly.'}`
        : "We couldn't refresh the site risk overview.";

    if (isLoading && leaderboard.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6">
                <div className="flex items-center justify-center h-48">
                    <RefreshCw className="w-6 h-6 animate-spin" style={{ color: '#69BE28' }} />
                    <span className="ml-2 text-gray-600">Loading site risk overview...</span>
                </div>
            </div>
        );
    }

    if (error && leaderboard.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6">
                <div className="flex flex-col items-center justify-center h-48 text-center">
                    <AlertTriangle className={`w-7 h-7 mb-3 ${rateLimited ? 'text-amber-500' : 'text-red-500'}`} />
                    <p className="text-gray-700">{refreshErrorMessage}</p>
                    <button onClick={() => void loadLeaderboard()} className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-800">
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (leaderboard.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                            <Building2 className="w-4 h-4" style={{ color: '#69BE28' }} />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Site Risk Overview</h3>
                    </div>
                </div>
                <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Set up your fleet to see site risk</p>
                    <p className="text-sm text-gray-400 mt-1">Create a site, then add or import drivers to begin monitoring risk.</p>
                    <button onClick={onViewAll} className="mt-4 px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: '#69BE28' }}>
                        Manage sites
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6">
            {error && (
                <RefreshErrorBanner
                    message={refreshErrorMessage}
                    rateLimited={rateLimited}
                    onRetry={() => void loadLeaderboard()}
                    rounded="lg"
                    compact
                    className="mb-4"
                />
            )}
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                        <Building2 className="w-4 h-4" style={{ color: '#69BE28' }} />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Site Risk Overview</h3>
                    <div className="relative group">
                        <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                        <div className="absolute left-0 top-6 w-80 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                            <p className="font-semibold text-sm mb-2">How Site Scores Work</p>
                            <p className="text-gray-300 mb-3">
                                Site Score = <span className="text-white font-medium">Average of all driver risk scores</span>. Lower is better.
                            </p>

                            <p className="text-gray-400 uppercase text-[10px] tracking-wide mb-2">Each driver scores</p>
                            <div className="space-y-1.5 mb-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-300">Recently checked</span>
                                    <span className="text-gray-500">Their licence check score</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-300">Never checked</span>
                                    <span className="text-gray-500">50 points (unknown risk)</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-300">Overdue for recheck</span>
                                    <span className="text-gray-500">Score + 15 points</span>
                                </div>
                            </div>

                            <p className="text-gray-400 text-[10px] mb-3">
                                Driver licence check scores factor in: penalty points, endorsements, disqualifications, licence status, serious offence codes, and more.
                            </p>

                            <div className="pt-2 border-t border-gray-700">
                                <p className="text-gray-400 uppercase text-[10px] tracking-wide mb-1.5">Risk levels (aligned with driver scoring)</p>
                                <div className="flex items-center space-x-4">
                                    <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>Low &lt;{lowMediumBoundary}</span>
                                    <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-amber-500 mr-1.5"></span>Medium {lowMediumBoundary}-{mediumHighBoundary - 1}</span>
                                    <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></span>High {mediumHighBoundary}+</span>
                                </div>
                            </div>

                            <div className="absolute -top-1.5 left-3 w-3 h-3 bg-gray-900 rotate-45"></div>
                        </div>
                    </div>
                </div>
                <button
                    onClick={onViewAll}
                    className="flex items-center text-sm font-medium hover:opacity-80 transition-opacity"
                    style={{ color: '#69BE28' }}
                >
                    View All {leaderboard.length}
                    <ArrowRight className="w-4 h-4 ml-1" />
                </button>
            </div>

            {/* Risk Distribution Summary */}
            <div className="grid mb-6" style={{ gridTemplateColumns: riskDistribution.noDrivers > 0 ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)', gap: '10px' }}>
                <div className="flex flex-col px-3 py-2.5 bg-gray-50 rounded-r-lg" style={{ borderLeft: '3px solid #69BE28' }}>
                    <span className="text-xl font-medium leading-none" style={{ color: '#69BE28' }}>{riskDistribution.low}</span>
                    <span className="text-xs text-gray-500 mt-1">Low risk</span>
                </div>
                <div className="flex flex-col px-3 py-2.5 bg-gray-50 rounded-r-lg" style={{ borderLeft: '3px solid #f59e0b' }}>
                    <span className="text-xl font-medium leading-none text-amber-500">{riskDistribution.medium}</span>
                    <span className="text-xs text-gray-500 mt-1">Medium risk</span>
                </div>
                <div className="flex flex-col px-3 py-2.5 bg-gray-50 rounded-r-lg" style={{ borderLeft: '3px solid #ef4444' }}>
                    <span className="text-xl font-medium leading-none text-red-500">{riskDistribution.high}</span>
                    <span className="text-xs text-gray-500 mt-1">High risk</span>
                </div>
                {riskDistribution.noDrivers > 0 && (
                    <div className="flex flex-col px-3 py-2.5 bg-gray-50 rounded-r-lg" style={{ borderLeft: '3px solid #9ca3af' }}>
                        <span className="text-xl font-medium leading-none text-gray-400">{riskDistribution.noDrivers}</span>
                        <span className="text-xs text-gray-500 mt-1">No drivers</span>
                    </div>
                )}
            </div>

            {/* Adaptive Layout: Two columns with top/bottom, or single grid if no top performers */}
            {topPerformers.length > 0 ? (
                // Two Section Layout - Top Performers + Needs Attention
                <div className="grid grid-cols-2 gap-6">
                    {/* Top Performers */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-600 mb-3 flex items-center">
                            <Trophy className="w-4 h-4 mr-1 text-yellow-500" />
                            Top Performers
                        </h4>
                        <div className="space-y-2">
                            {topPerformers.map((site) => (
                                <button
                                    key={site.siteId}
                                    onClick={() => handleSiteClick(site.siteId)}
                                    className="w-full flex items-center justify-between px-3 py-2 bg-green-50 rounded-lg hover:bg-green-100 transition-colors cursor-pointer text-left"
                                >
                                    <div className="flex items-center min-w-0">
                                        <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-semibold mr-2 flex-shrink-0">{site.rank}</span>
                                        <span className="text-sm font-medium text-gray-800 truncate">{site.siteName}</span>
                                    </div>
                                    <div className="flex items-center flex-shrink-0 ml-2">
                                        {getTrendIcon(site.trend)}
                                        <span
                                            className="ml-1 px-2 py-0.5 rounded text-xs font-bold text-white"
                                            style={{ backgroundColor: getRiskColor(site.riskLevel) }}
                                        >
                                            {site.totalScore.toFixed(0)}
                                        </span>
                                        <ChevronRight className="w-4 h-4 ml-1 text-gray-400" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Needs Attention */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-600 mb-3 flex items-center">
                            <AlertTriangle className="w-4 h-4 mr-1 text-amber-500" />
                            Needs Attention
                        </h4>
                        <div className="space-y-2">
                            {needsAttention.length > 0 ? (
                                needsAttention.map((site, index) => (
                                    <button
                                        key={site.siteId}
                                        onClick={() => handleSiteClick(site.siteId)}
                                        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-left"
                                    >
                                        <div className="flex items-center min-w-0">
                                            <span className="text-xs font-medium text-gray-400 mr-2">{index + 1}</span>
                                            <span className="text-sm font-medium text-gray-800 truncate">{site.siteName}</span>
                                        </div>
                                        <div className="flex items-center flex-shrink-0 ml-2">
                                            {getTrendIcon(site.trend)}
                                            <span
                                                className="ml-1 px-2 py-0.5 rounded text-xs font-bold text-white"
                                                style={{ backgroundColor: getRiskColor(site.riskLevel) }}
                                            >
                                                {site.totalScore.toFixed(0)}
                                            </span>
                                            <ChevronRight className="w-4 h-4 ml-1 text-gray-400" />
                                        </div>
                                    </button>
                                ))
                            ) : (
                                <div className="flex items-center justify-center py-6 bg-green-50 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                                    <span className="text-sm text-green-700 font-medium">All sites compliant!</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                // Compact 2-column grid - shows top worst sites only
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-600">
                            Sites Needing Attention
                        </h4>
                        <span className="text-xs text-gray-400">
                            Aim for &lt;{lowMediumBoundary}
                        </span>
                    </div>
                    {needsAttention.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3">
                            {needsAttention.map((site, index) => (
                                <button
                                    key={site.siteId}
                                    onClick={() => handleSiteClick(site.siteId)}
                                    className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-left"
                                >
                                    <div className="flex items-center min-w-0">
                                        <span className="text-xs font-medium text-gray-400 mr-2">{index + 1}</span>
                                        <span className="text-sm font-medium text-gray-800 truncate">{site.siteName}</span>
                                    </div>
                                    <div className="flex items-center flex-shrink-0 ml-2">
                                        {getTrendIcon(site.trend)}
                                        <span
                                            className="ml-1 px-2 py-0.5 rounded text-xs font-bold text-white"
                                            style={{ backgroundColor: getRiskColor(site.riskLevel) }}
                                        >
                                            {site.totalScore.toFixed(0)}
                                        </span>
                                        <ChevronRight className="w-4 h-4 ml-1 text-gray-400" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center py-6 bg-green-50 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                            <span className="text-sm text-green-700 font-medium">All sites compliant!</span>
                        </div>
                    )}
                </div>
            )}

        </div>
    );
};

export default SitesWidget;
